import { useEffect, useMemo, useRef, useState } from 'react';
import { hashKey, InfiniteData, useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from 'src/auth/hooks';
import { usePermissions, RESOURCES } from 'src/hooks/use-permissions';
import {
  Chat,
  ChatMessage,
  ChatMessagesResponse,
  ChatUnreadCountResponse,
  ChatsResponse,
} from 'src/types';
import { getPreviewAttachmentKind } from 'src/utils/chat-attachment';
import { isSuccessResponse } from 'src/utils/response-type-guards';

import { createChatRealtimeClient } from './create-client';
import { ChatRealtimeClient, ChatSocketStatus } from './types';
import { chatKeys } from 'src/utils/query-keys';
import { ChatRealtimeContext, ChatRealtimeContextValue } from './context';

// ----------------------------------------------------------------------
// App-wide chat realtime connection. Mounted around DashboardLayout so the
// sidebar unread badge stays live without visiting the chat page. All socket
// events funnel into the React Query cache here — surgically, via
// setQueryData — making the cache the single source of truth for the UI.
// ----------------------------------------------------------------------

const toPreview = (message: ChatMessage) => ({
  id: message.id,
  seq: message.seq,
  sender: message.sender,
  text: message.text,
  has_attachments: message.attachments.length > 0,
  attachment_kind: getPreviewAttachmentKind(message.attachments),
  created_at: message.created_at,
});

type Props = {
  children: React.ReactNode;
};

export function ChatRealtimeProvider({ children }: Props) {
  const { platformId, user } = useAuthContext();
  const { canGet } = usePermissions();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ChatSocketStatus>('idle');
  const [degradedChannels, setDegradedChannels] = useState<string[]>([]);

  const clientRef = useRef<ChatRealtimeClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = createChatRealtimeClient();
  }
  const client = clientRef.current;

  const userIdRef = useRef<string>('');
  userIdRef.current = user?.id ?? '';

  const enabled = !!platformId && canGet(RESOURCES.CHAT.MAIN);

  useEffect(() => {
    if (!enabled) return undefined;
    client.connect({ platformId });
    return () => {
      client.disconnect();
    };
  }, [client, enabled, platformId]);

  useEffect(() => {
    if (!enabled) return undefined;

    const listKey = chatKeys.list(platformId);
    const listHash = hashKey(listKey);

    const syncChannels = () => {
      const list = queryClient.getQueryData<ChatsResponse>(listKey);
      const channels =
        list && isSuccessResponse(list)
          ? list.items.map((chat) => chat.channel).filter(Boolean)
          : [];
      client.setChannels(channels);
    };

    syncChannels();
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryHash !== listHash) return;
      syncChannels();
    });

    return () => {
      unsubscribe();
      client.setChannels([]);
    };
  }, [client, enabled, platformId, queryClient]);

  useEffect(() => client.onDegradedChange(setDegradedChannels), [client]);

  // Reconnect resync: after any drop, one prefix invalidation recovers the
  // list, unread counts and any open thread (all chat keys share the prefix).
  useEffect(() => {
    if (!platformId) return undefined;
    let hadDrop = false;
    return client.onStatusChange((next) => {
      setStatus(next);
      if (next === 'reconnecting') hadDrop = true;
      if (next === 'open' && hadDrop) {
        hadDrop = false;
        queryClient.invalidateQueries({ queryKey: chatKeys.list(platformId) });
      }
    });
  }, [client, platformId, queryClient]);

  useEffect(() => {
    if (!platformId) return undefined;

    const listKey = chatKeys.list(platformId);
    const unreadKey = chatKeys.unread(platformId);

    const updateChatInList = (chatId: string, updater: (chat: Chat) => Chat, moveToTop = false) => {
      queryClient.setQueryData<ChatsResponse>(listKey, (old) => {
        if (!old || !isSuccessResponse(old)) return old;
        const index = old.items.findIndex((chat) => chat.id === chatId);
        if (index === -1) return old;
        const updated = updater(old.items[index]);
        const items = old.items.slice();
        items[index] = updated;
        if (moveToTop && index > 0) {
          items.splice(index, 1);
          items.unshift(updated);
        }
        return { ...old, items };
      });
    };

    const isChatInList = (chatId: string) => {
      const list = queryClient.getQueryData<ChatsResponse>(listKey);
      return !!list && isSuccessResponse(list) && list.items.some((chat) => chat.id === chatId);
    };

    const unsubscribers = [
      client.on('chat.message.new', ({ message }) => {
        const mine = message.sender.id === userIdRef.current;

        queryClient.setQueryData<InfiniteData<ChatMessagesResponse>>(
          chatKeys.messages(platformId, message.chat_id),
          (old) => {
            if (!old || !old.pages.length) return old;
            const matches = (cached: ChatMessage) =>
              cached.id === message.id ||
              (!!message.client_id && cached.client_id === message.client_id);
            const exists = old.pages.some(
              (page) => isSuccessResponse(page) && page.items.some(matches)
            );
            if (exists) {
              // Echo of a message we already show (optimistic or REST
              // response) — replace in place instead of appending twice.
              return {
                ...old,
                pages: old.pages.map((page) =>
                  isSuccessResponse(page)
                    ? { ...page, items: page.items.map((m) => (matches(m) ? message : m)) }
                    : page
                ),
              };
            }
            const [newest, ...rest] = old.pages;
            if (!isSuccessResponse(newest)) return old;
            return { ...old, pages: [{ ...newest, items: [...newest.items, message] }, ...rest] };
          }
        );

        if (!isChatInList(message.chat_id)) {
          queryClient.invalidateQueries({ queryKey: listKey, exact: true });
        } else {
          updateChatInList(
            message.chat_id,
            (chat) => ({
              ...chat,
              last_message: toPreview(message),
              unread_count: mine ? chat.unread_count : chat.unread_count + 1,
            }),
            true
          );
          if (!mine) {
            queryClient.setQueryData<ChatsResponse>(listKey, (old) =>
              old && isSuccessResponse(old) ? { ...old, total_unread: old.total_unread + 1 } : old
            );
          }
        }
        if (!mine) {
          queryClient.setQueryData<ChatUnreadCountResponse>(unreadKey, (old) =>
            old && isSuccessResponse(old) ? { total_unread: old.total_unread + 1 } : old
          );
        }
      }),

      client.on('chat.receipts', (data) => {
        if (data.user_id === userIdRef.current) return;
        updateChatInList(data.chat_id, (chat) => ({
          ...chat,
          others_max_read_seq: Math.max(chat.others_max_read_seq, data.last_read_seq),
          others_max_delivered_seq: Math.max(
            chat.others_max_delivered_seq,
            data.last_delivered_seq
          ),
        }));
      }),

      client.on('chat.unread', (data) => {
        updateChatInList(data.chat_id, (chat) => ({
          ...chat,
          unread_count: data.unread_count,
          my_last_read_seq: Math.max(chat.my_last_read_seq, data.my_last_read_seq),
        }));
        queryClient.setQueryData<ChatsResponse>(listKey, (old) =>
          old && isSuccessResponse(old) ? { ...old, total_unread: data.total_unread } : old
        );
        queryClient.setQueryData<ChatUnreadCountResponse>(unreadKey, {
          total_unread: data.total_unread,
        });
      }),

      client.on('chat.created', ({ chat }) => {
        queryClient.setQueryData<ChatsResponse>(listKey, (old) => {
          if (!old || !isSuccessResponse(old)) return old;
          if (old.items.some((item) => item.id === chat.id)) return old;
          return { ...old, items: [chat, ...old.items] };
        });
        queryClient.invalidateQueries({
          queryKey: chatKeys.availableSources(platformId),
        });
      }),

      client.on('chat.updated', ({ chat }) => {
        if (!isChatInList(chat.id)) {
          queryClient.invalidateQueries({ queryKey: listKey, exact: true });
          return;
        }
        updateChatInList(chat.id, (existing) => ({ ...existing, ...chat }));
      }),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [client, platformId, queryClient]);

  const value = useMemo<ChatRealtimeContextValue>(
    () => ({ status, degradedChannels }),
    [status, degradedChannels]
  );

  return <ChatRealtimeContext.Provider value={value}>{children}</ChatRealtimeContext.Provider>;
}
